import { c as _c } from "react/compiler-runtime";
import { gql, useQuery } from '@material-bank/graphql-client';
import { isSettled } from '../utils/isSettled';
import { getDeepestCategorySlug } from '../utils/getDeepestCategorySlug';
import type { ProductByVariantSkusResult } from './useProductByVariantSkusQuery';

// Shared by the live query and the PDP prefetch so both hit the same cache entry —
// a different page size would key a separate entry and waste the warm-up.
export const SIMILAR_PRODUCTS_PER_PAGE = 50;
export const SIMILAR_PRODUCTS_QUERY = gql`
  query SimilarProducts(
    $sku: String!
    $variationSku: String
    $categoryGroupIds: [String!]
    $resultsPerPage: Int
    $locale: String
  ) {
    similarProducts(
      sku: $sku
      variationSku: $variationSku
      categoryGroupIds: $categoryGroupIds
      resultsPerPage: $resultsPerPage
      locale: $locale
    ) {
      products {
        id
        name
        brand
        imageUrl
        installImageUrl
        groupId
        hoverImage {
          url
          label
        }
        mainImage {
          url
          label
        }
        isOutOfStock
        searchHexCode
      }
      totalCount
    }
  }
`;
export type SimilarProductImage = {
  url: string;
  label: string | null;
};
export type SimilarProduct = {
  id: string;
  name: string;
  brand: string | null;
  imageUrl: string;
  installImageUrl: string | null;
  groupId: string | null;
  hoverImage: SimilarProductImage | null;
  mainImage: SimilarProductImage | null;
  isOutOfStock: boolean;
  searchHexCode: string | null;
};
export type SimilarProductsResult = {
  products: SimilarProduct[];
  totalCount: number;
};
type SimilarProductsQueryData = {
  similarProducts: SimilarProductsResult;
};
export type SimilarProductsQueryVars = {
  sku: string;
  variationSku?: string | null;
  categoryGroupIds?: string[] | null;
  resultsPerPage?: number | null;
  locale?: string | null;
};

// The one place Similar's variables are derived. The Similar page and the PDP
// prefetch both call it: Apollo keys the cache entry on every argument, and a
// `null` differs from an omitted one, so any drift makes the prefetch warm an
// entry the page never reads.
export function buildSimilarProductsVariables(product: ProductByVariantSkusResult | null, activeVariant: string | null, locale: string): SimilarProductsQueryVars {
  // `activeVariant` starts as the tag's productRef, which is not always a real
  // variant SKU — Constructor silently ignores an unknown variation_id, but we
  // still prefer a value we know resolves to a colorway.
  const isKnownVariant = Boolean(product?.variants.some(variant => variant.sku === activeVariant));
  const variationSku = isKnownVariant ? activeVariant : product?.masterVariantSKU ?? null;
  const slug = getDeepestCategorySlug(product?.categories ?? []);
  return {
    sku: product?.sku ?? '',
    variationSku,
    categoryGroupIds: slug ? [slug] : null,
    resultsPerPage: SIMILAR_PRODUCTS_PER_PAGE,
    locale
  };
}
export function useSimilarProductsQuery(options) {
  const $ = _c(13);
  const t0 = useQuery(SIMILAR_PRODUCTS_QUERY, options);
  let data;
  let networkStatus;
  let previousData;
  let rest;
  if ($[0] !== t0) {
    ({
      data,
      previousData,
      networkStatus,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = data;
    $[2] = networkStatus;
    $[3] = previousData;
    $[4] = rest;
  } else {
    data = $[1];
    networkStatus = $[2];
    previousData = $[3];
    rest = $[4];
  }
  const t1 = data?.similarProducts;
  const t2 = previousData?.similarProducts;
  let t3;
  if ($[5] !== networkStatus) {
    t3 = isSettled(networkStatus);
    $[5] = networkStatus;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== networkStatus || $[8] !== rest || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      data: t1,
      previousData: t2,
      networkStatus,
      settled: t3,
      ...rest
    };
    $[7] = networkStatus;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}